import { action, computed, observable, runInAction } from 'mobx';
import { del, get } from '../../../services/request';

import app from '../../App/stores/AppStore';
import user from '../../App/stores/UserStore';
import listings from '../../Overview/stores/ListingsStore';
import sale from '../../Sale/stores/SaleStore';

const byDefaultDoNothing = () => {};
const overviewColumns = (t, onClick = byDefaultDoNothing) => [
  {
    key: 'image',
    fieldName: 'image',
    maxWidth: 100,
  },
  {
    key: 'referenceNumber',
    name: t('Number'),
    fieldName: 'referenceNumber',
    minWidth: 50,
    maxWidth: 130,
    onColumnClick: onClick,
    isSorted: false,
    isSortedDescending: false,
    isResizable: true,
  },
  {
    key: 'title',
    name: t('Title'),
    fieldName: 'title',
    minWidth: 200,
    maxWidth: 600,
    onColumnClick: onClick,
    isSorted: false,
    isSortedDescending: false,
    isResizable: true,
  },
  {
    key: 'address',
    name: t('Address'),
    fieldName: 'address',
    minWidth: 200,
    isResizable: true,
  },
  {
    key: 'price',
    name: t('Price'),
    fieldName: 'price',
    className: 'ms-textAlignRight',
    headerClassName: 'ms-textAlignRight',
    maxWidth: 100,
    isResizable: true,
  },
  {
    key: 'area',
    name: `${t('Area')} (${t('Area_unit')})`,
    fieldName: 'area',
    className: 'ms-textAlignCenter',
    headerClassName: 'ms-textAlignCenter',
    maxWidth: 100,
    isResizable: true,
  },
];

class InventoryStore {
  @observable items = [];
  @observable columns = [];
  @observable selectedItem = null;

  @observable fetching = true;
  @observable error = null;
  @observable fetchingItem = false;

  @observable selectedAgent;
  @observable agents = [];

  constructor(app, sale, user, listings) {
    this.app = app;
    this.sale = sale;
    this.user = user;
    this.listings = listings;
  }

  @computed get saleAgent() {
    const agent = this.selectedItem.public.agent;

    return (
      this.selectedAgent ||
      this.agents.find((a) => String(a.sub) === String(agent.sub)) ||
      this.agents.find(
        (a) =>
          (String(a.id) || String(a.agent_id)) === String(agent.id) &&
          ((!a.office_id && !agent.office_id) ||
            String(a.office_id) === String(agent.office_id))
      ) ||
      (this.agents.length === 1 ? this.agents[0] : undefined)
    );
  }

  @computed get agentsForSelectedItem() {
    const office_id =
      !!this.selectedItem && !!this.selectedItem.private
        ? this.selectedItem.private.office_id
        : null;

    if (!office_id) return this.agents;

    return this.agents.filter(
      (a) => !a.office_id || String(a.office_id) === String(office_id)
    );
  }

  @computed get masterSales() {
    var filtered = this.listings.items
      .filter((i) => !!(i.features || {}).isMasterSale)
      .map((s) => ({ key: s.id, text: s.title }));
    return filtered;
  }

  @computed get redirect() {
    const { commandType, result } = this.sale.response;
    if (commandType !== 'StartSaleProcess') {
      return false;
    }
    if (!result || !result.successful) {
      return false;
    }
    return true;
  }

  @computed get isListed() {
    return this.listed !== null;
  }

  @computed get listed() {
    if (!this.selectedItem) return null;
    var selected = this.selectedItem.private.objectno;
    var filtered = this.listings.items.filter(
      (i) => i.status !== 'Resolved' && i.itemNumber === selected
    );
    return filtered.length > 0 ? filtered[0] : null;
  }

  @computed get startedSale() {
    const { command } = this.sale.response;
    return command ? command.saleId : undefined;
  }

  async findListing(number) {
    var filtered = this.listings.items.filter(
      (i) => i.status !== 'Resolved' && i.itemNumber === number
    );
    return filtered.length > 0 ? filtered[0] : null;
  }

  @action async loadAgents() {
    try {
      const um =
        this.app.config &&
        this.app.config.userManagement &&
        this.app.config.userManagement.url
          ? this.app.config.userManagement.url
          : '';
      const result = await get(`${um}/users/lookup`, this.user.accessToken);
      runInAction(() => (this.agents = result));
    } catch (e) {}
  }

  @action initColumns(t, onClick) {
    this.columns = overviewColumns(t, onClick);
  }

  @action sortBy(field) {
    const index = this.columns.findIndex((c) => c.key === field);
    const column = this.columns[index];
    this.items = this.items.sortBy(field, column.isSortedDescending);

    this.columns = this.columns.map((c, i) => {
      if (i !== index) {
        c.isSorted = false;
        c.isSortedDescending = false;
      } else {
        c.isSorted = true;
        c.isSortedDescending = !c.isSortedDescending;
      }
      return c;
    });
  }

  @action async select(item) {
    await this.get(item.id);
  }

  @action deselect(item) {
    this.selectedItem = null;
  }

  @action search() {
    this.fetching = true;
    this.error = null;
    if (!listings.loaded) {
      listings.load();
    }
    get(this.app.config.inventory.url, this.user.accessToken).then(
      this.searchSuccess,
      this.onError
    );
  }

  @action async get(id) {
    this.fetchingItem = true;
    await get(
      `${this.app.config.inventory.url}${
        this.app.config.inventory.url.endsWith('/') ? '' : '/'
      }${encodeURIComponent(id)}`,
      this.user.accessToken
    ).then(
      (item) => this.getSuccess(Object.assign(item, { inventoryId: id })),
      this.onError
    );
  }

  @action async getExistingSale(id) {
    try {
      const result = await get(
        `/api/v1/sales/find-by-integration-id?id=${encodeURIComponent(id)}`,
        this.user.accessToken
      );

      return result ? result.saleIds.at(0) : null;
    } catch (e) {
      return null;
    }
  }

  @action.bound searchSuccess(items) {
    this.items = items;
    this.fetching = false;
    this.error = null;
  }

  @action.bound getSuccess(item) {
    const supportedLocales = this.user.userConfig.localeCodes;
    const locale =
      supportedLocales.indexOf(user.claims.locale) >= 0
        ? user.claims.locale
        : supportedLocales[0];

    item.public = Object.assign(
      {
        currency_code: this.app.currency.code,
        locale,
      },
      item.public
    );
    this.selectedItem = item;
    this.fetchingItem = false;
  }

  @action.bound onError(error) {
    this.items = [];
    this.fetching = false;
    this.error = error;
  }

  @action async listBuyers(id, saleId, contacts) {
    const query = contacts ? `?contacts=${contacts}` : '';
    return get(
      `${this.app.config.inventory.url}${
        this.app.config.inventory.url.endsWith('/') ? '' : '/'
      }${encodeURIComponent(id) || 'empty'}/buyers${query}`,
      this.user.accessToken,
      { saleId }
    );
  }

  @action delete(id) {
    return del(
      `${this.app.config.inventory.url}${
        this.app.config.inventory.url.endsWith('/') ? '' : '/'
      }${encodeURIComponent(id)}`,
      this.user.accessToken
    );
  }
}

const inventory = new InventoryStore(app, sale, user, listings);

export default inventory;
export { InventoryStore };



// WEBPACK FOOTER //
// ./src/app/containers/Search/stores/InventoryStore.js